/**
 * Keep CGD values for still-selected company sources; add empty entries for new ones.
 *
 * @param {Record<string|number, string|number|undefined>} previousByCompanySourceID
 * @param {Array<number|string>} companySourceIDs
 * @returns {Record<string, string>}
 */
export function syncCustomerGuaranteeDaysByCompanySource(previousByCompanySourceID = {}, companySourceIDs = []) {
    const next = {};
    companySourceIDs.forEach((id) => {
        const key = String(id);
        const previous = previousByCompanySourceID[key] ?? previousByCompanySourceID[id];
        next[key] = previous == null ? '' : String(previous);
    });
    return next;
}

/**
 * Normalize list/API payload into `{ companySourceID, customerGuaranteeDays }[]`.
 *
 * @param {unknown} entries
 * @returns {Array<{companySourceID: number, customerGuaranteeDays: number}>}
 */
export function normalizeCustomerGuaranteeDaysEntries(entries) {
    let list = entries;
    if (typeof list === 'string') {
        try {
            list = JSON.parse(list);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(list)) {
        return [];
    }

    return list
        .map((entry) => {
            if (entry == null || typeof entry !== 'object') {
                return null;
            }
            const companySourceID = Number(entry.companySourceID ?? entry.companySourceId);
            const rawDays = entry.customerGuaranteeDays;
            // Number(null) and Number('') are 0 — treat missing days as absent, not zero.
            if (rawDays == null || rawDays === '') {
                return null;
            }
            const customerGuaranteeDays = Number(rawDays);
            if (!Number.isFinite(companySourceID) || !Number.isFinite(customerGuaranteeDays)) {
                return null;
            }
            return { companySourceID, customerGuaranteeDays };
        })
        .filter(Boolean);
}

/**
 * Compact chip labels for the item-master table cell.
 *
 * @param {Object} args
 * @param {unknown} [args.entries]
 * @param {Array<{companySourceId?: number, value?: number, id?: number, companySourceName?: string, text?: string}>} [args.companySources]
 * @returns {Array<{companySourceID: number, customerGuaranteeDays: number, label: string}>}
 */
export function buildCustomerGuaranteeDaysChips({ entries = [], companySources = [] } = {}) {
    const nameById = new Map();
    companySources.forEach((source) => {
        const id = source.companySourceId ?? source.value ?? source.id;
        if (id == null) {
            return;
        }
        const name = source.companySourceName || source.text || `Company source ${id}`;
        nameById.set(Number(id), name);
    });

    return normalizeCustomerGuaranteeDaysEntries(entries).map(({ companySourceID, customerGuaranteeDays }) => ({
        companySourceID,
        customerGuaranteeDays,
        label: `${nameById.get(companySourceID) || `Company source ${companySourceID}`}: ${customerGuaranteeDays}`,
    }));
}

/**
 * Prefill from per-source attrs list only (`customerGuaranteeDaysByCompanySource`).
 * Missing sources stay empty — no item_information.customer_guarantee_days fallback.
 *
 * @param {Object} args
 * @param {Array<number|string>} args.companySourceIDs
 * @param {unknown} [args.customerGuaranteeDaysByCompanySource]
 * @returns {Record<string, string>}
 */
export function buildCustomerGuaranteeDaysPrefill({
    companySourceIDs = [],
    customerGuaranteeDaysByCompanySource = [],
} = {}) {
    const fromList = {};
    normalizeCustomerGuaranteeDaysEntries(customerGuaranteeDaysByCompanySource).forEach((entry) => {
        fromList[String(entry.companySourceID)] = String(entry.customerGuaranteeDays);
    });
    return syncCustomerGuaranteeDaysByCompanySource(fromList, companySourceIDs);
}

/**
 * Variant create (existing II / external SKU selected) must not require or POST CGD —
 * attrs stay on the shared SKU. New II create still collects and submits CGD.
 *
 * @param {Object} args
 * @param {boolean} args.isExistingItemInformation
 * @returns {boolean}
 */
export function shouldSubmitCustomerGuaranteeDaysOnCreate({ isExistingItemInformation }) {
    return !isExistingItemInformation;
}

/**
 * Prefill CGD after map-add from each add response's existingCustomerGuaranteeDays.
 * Attrs can survive an earlier unmap; missing/null values stay empty so the user must enter them.
 *
 * @param {Object} args
 * @param {Array<number|string>} args.companySourceIDs
 * @param {Record<string|number, { data?: { existingCustomerGuaranteeDays?: number|null } }>} [args.addResponsesByCompanySourceID]
 * @returns {Record<string, string>}
 */
export function buildCustomerGuaranteeDaysPrefillFromAddResponses({
    companySourceIDs = [],
    addResponsesByCompanySourceID = {},
} = {}) {
    const fromResponses = {};
    companySourceIDs.forEach((id) => {
        const response = addResponsesByCompanySourceID[String(id)] ?? addResponsesByCompanySourceID[id];
        const days = response?.data?.existingCustomerGuaranteeDays;
        if (days == null || days === '') {
            return;
        }
        if (!Number.isFinite(Number(days))) {
            return;
        }
        fromResponses[String(id)] = String(days);
    });
    return syncCustomerGuaranteeDaysByCompanySource(fromResponses, companySourceIDs);
}

/**
 * Company-source IDs that still need a CGD value after map-add.
 * Sources with surviving existingCustomerGuaranteeDays do not need a prompt.
 *
 * @param {Object} args
 * @param {Array<number|string>} args.companySourceIDs
 * @param {Record<string|number, { data?: { existingCustomerGuaranteeDays?: number|null } }>} [args.addResponsesByCompanySourceID]
 * @returns {number[]}
 */
export function getCompanySourceIDsNeedingCgdFromAddResponses({
    companySourceIDs = [],
    addResponsesByCompanySourceID = {},
} = {}) {
    return companySourceIDs
        .map((id) => Number(id))
        .filter((id) => {
            if (!Number.isFinite(id) || id <= 0) {
                return false;
            }
            const response = addResponsesByCompanySourceID[String(id)] ?? addResponsesByCompanySourceID[id];
            const days = response?.data?.existingCustomerGuaranteeDays;
            if (days == null || days === '') {
                return true;
            }
            return !Number.isFinite(Number(days));
        });
}

/**
 * Copy one source's value onto every other selected company source.
 *
 * @param {Record<string, string>} byCompanySourceID
 * @param {number|string} sourceCompanySourceID
 * @returns {Record<string, string>}
 */
export function applyCustomerGuaranteeDaysToAll(byCompanySourceID, sourceCompanySourceID) {
    const sourceKey = String(sourceCompanySourceID);
    const value = byCompanySourceID[sourceKey];
    if (value == null || value === '') {
        return { ...byCompanySourceID };
    }

    const next = { ...byCompanySourceID };
    Object.keys(next).forEach((key) => {
        next[key] = String(value);
    });
    return next;
}

/**
 * @param {Record<string, string>} byCompanySourceID
 * @param {Array<number|string>} companySourceIDs
 * @returns {boolean}
 */
export function areAllCustomerGuaranteeDaysFilled(byCompanySourceID, companySourceIDs) {
    if (!companySourceIDs.length) {
        return false;
    }
    return companySourceIDs.every((id) => {
        const value = byCompanySourceID[String(id)];
        if (value == null || value === '') {
            return false;
        }
        const asNumber = Number(value);
        return Number.isFinite(asNumber) && asNumber >= 0;
    });
}

/**
 * @param {Record<string, string>} byCompanySourceID
 * @param {Array<number|string>} companySourceIDs
 * @returns {Array<{companySourceID: number, customerGuaranteeDays: number}>}
 */
export function buildCustomerGuaranteeDaysByCompanySourcePayload(byCompanySourceID, companySourceIDs) {
    return companySourceIDs.map((id) => ({
        companySourceID: Number(id),
        customerGuaranteeDays: Number(byCompanySourceID[String(id)]),
    }));
}
